// PATH: src/api/monitoring/v2.ts
/**
 * Client de l'API Monitoring v2 (tc_tilmicode_be).
 *
 * Les types reproduisent `docs/monitoring/CONTRAT_API_V2.md`, qui fait foi :
 * toute évolution du contrat se fait là-bas d'abord, puis ici.
 *
 * Règles de lecture côté front :
 * - aucun calcul de métrique : le front formate et affiche ce que le serveur
 *   renvoie (un ratio arrive déjà arrondi, un total déjà sommé) ;
 * - l'argent n'est jamais un flottant : `amount` est une chaîne décimale et la
 *   devise est toujours portée par la valeur ;
 * - les états vides viennent de `empty_reasons`, jamais d'un test à 0.
 *
 * Ce module est pur (types, constantes, fonctions par endpoint sur un client
 * fourni) : le hook qui le branche sur `useApiClient` est dans
 * `useMonitoringV2Api.ts`.
 */
import type { AxiosInstance } from 'axios';

const PREFIX = '/api/monitoring/v2';

export const ENDPOINTS_MONITORING_V2 = {
    SCOPES: `${PREFIX}/scopes`,
    PEDAGOGY: `${PREFIX}/pedagogy`,
    AI: `${PREFIX}/ai`,
    TRACES: `${PREFIX}/traces`,
    trace: (callId: string) => `${PREFIX}/traces/${encodeURIComponent(callId)}`,
    FINANCE: `${PREFIX}/finance`,
    exportDataset: (dataset: ExportDataset) => `${PREFIX}/export/${dataset}`,
    AI_PRICES: `${PREFIX}/ai-prices`,
};

// ============================================================
// Types de valeurs (contrat §1)
// ============================================================

/** Date locale `YYYY-MM-DD` (fuseau métier). */
export type IsoDate = string;
/**
 * ISO 8601 avec décalage local numérique, ex. `2026-10-05T10:10:00+00:00`.
 * Affiché à l'heure murale qu'il porte, jamais reconverti (contrat v2.2 §11).
 */
export type IsoDateTime = string;
/** Chaîne décimale exacte, ex. `"150.00"`. Jamais un nombre JS. */
export type DecimalString = string;

export type Money = { amount: DecimalString; currency: string };
/** Une entrée par devise, jamais additionnées entre elles. */
export type MoneyMulti = Money[];

export type CostStatus = 'computed' | 'partial' | 'none';
export type Cost = { amount: DecimalString; currency: string; status: CostStatus };

/** `value` : pourcentage déjà arrondi par le serveur, `null` si `den = 0`. */
export type Ratio = { value: DecimalString | null; num: number; den: number };

export const SCOPE_TYPES = ['all', 'school', 'parent', 'student'] as const;
export type ScopeType = (typeof SCOPE_TYPES)[number];

export const EMPTY_REASONS = [
    'future_period',
    'no_activity',
    'no_students',
    'no_ai_calls',
    'no_payments',
    'no_invoices',
] as const;
export type EmptyReason = (typeof EMPTY_REASONS)[number];

export type PeriodKind = 'past' | 'current' | 'future';
export type Grain = 'day' | 'week' | 'month';

export type Meta = {
    period: {
        from: IsoDate;
        to: IsoDate;
        tz: string;
        kind: PeriodKind;
        as_of: IsoDate;
        grain: Grain;
    };
    scope: { type: ScopeType; id: number | null; name: string | null };
    viewer: { can_view_all: boolean; can_view_costs: boolean };
    generated_at: string;
    catalog_version: string;
};

/** Enveloppe commune de toutes les réponses de données. */
export type Envelope = {
    meta: Meta;
    empty_reasons: EmptyReason[];
};

// ============================================================
// Séries et tableaux
// ============================================================

/**
 * `t` : `YYYY-MM-DD` (jour, ou lundi ISO pour la semaine) ou `YYYY-MM` (mois).
 * `v` : entier pour un comptage, chaîne décimale pour une série monétaire.
 */
export type SeriesPoint = { t: string; v: number | DecimalString };

export type Series = {
    metric: string;
    grain: Grain;
    unit: string;
    /** Présente sur les séries monétaires : une série par devise. */
    currency?: string;
    /**
     * Séries ventilées (§10.1) : objet à une clé, `source` ou `actor_kind`,
     * ex. `{"source":"chat"}`.
     */
    dimension?: Partial<Record<'source' | 'actor_kind', string>>;
    points: SeriesPoint[];
};

export type ColumnType =
    | 'text'
    | 'count'
    | 'money'
    | 'money_multi'
    | 'cost'
    | 'ratio'
    | 'date'
    | 'datetime'
    // Types composés précisés par le §10.6
    | 'tokens'
    | 'cost_status_counts'
    | 'learner'
    | `enum:${string}`;

/** Forme IA-04 ; `null` = inconnu (§10.6). */
export type Tokens = { input: number | null; cached: number | null; output: number | null; reasoning: number | null };

/** Statut de valorisation d'un appel (`monitoring.v_ai_call`, §10.4). */
export const COST_STATUSES = ['computed', 'no_price', 'no_usage', 'not_billable'] as const;
export type CostStatusCode = (typeof COST_STATUSES)[number];
export type CostStatusCounts = Record<CostStatusCode, number>;

/** Élève d'un appel : nom `null` hors de la branche de l'observateur (§10.6). */
export type Learner = { id: number; name: string | null } | null;

export type TableColumn = {
    key: string;
    type: ColumnType;
    /** Clé i18n du libellé, ex. `monitoring.col.name`. */
    label: string;
};

export type Table<Row = Record<string, unknown>> = {
    columns: TableColumn[];
    rows: Row[];
    total_rows: number;
    limit: number;
    offset: number;
};

// ============================================================
// Paramètres
// ============================================================

export type ScopeParams = {
    scope_type: ScopeType;
    /** Obligatoire sauf pour `all`. */
    scope_id?: number;
};

export type PeriodParams = {
    /** Bornes incluses, dates locales au fuseau métier. */
    from: IsoDate;
    to: IsoDate;
};

export type CommonParams = ScopeParams & PeriodParams;

export type SortOrder = 'asc' | 'desc';

/**
 * Tri, recherche et pagination d'UN tableau, faits par le serveur. Envoyés
 * préfixés par la clé du tableau (§10.2) : `students.sort`, `invoices.limit`…
 */
export type TableQuery = {
    sort?: string;
    order?: SortOrder;
    q?: string;
    /** Maximum 200. */
    limit?: number;
    offset?: number;
};

/** Paramètres d'un tableau : tri/pagination et filtres propres (§10.3). */
export type TableRequest<Filters extends object = Record<never, never>> = TableQuery & Filters;

export const TABLE_LIMIT_MAX = 200;

export const AI_SOURCES = ['chat', 'learn', 'embed', 'audio'] as const;
export type AiSource = (typeof AI_SOURCES)[number];
export type AiSourceFilter = 'all' | AiSource;

export type LangCode = 'fr' | 'en' | 'ar';

// ============================================================
// §2 — GET /scopes
// ============================================================

export type ScopesResponse = {
    /**
     * Jour courant du fuseau métier, calculé par le serveur (v2.2) : point de
     * départ de tous les raccourcis de période, à la place de l'horloge et du
     * fuseau du navigateur.
     */
    today: IsoDate;
    /** Nom du fuseau métier, informatif (aucun calcul ne s'en sert). */
    tz: string;
    can_view_all: boolean;
    schools: Array<{ id: number; name: string; students: number }>;
    parents: Array<{ id: number; name: string; students: number }>;
    students: Array<{ id: number; name: string; parent_id: number | null; parent_name: string | null }>;
};

// ============================================================
// §3 — GET /pedagogy
// ============================================================

export type PedagogyTiles = {
    students_followed: number;
    students_connected: number;
    students_active: number;
    engagement: Ratio;
    lessons_completed: number;
    exercises_submitted: { value: number; of_which_last_state: number };
    projects_created: number;
    sessions: number;
    active_minutes: number;
};

export type PedagogySubjectRow = { subject: string; lessons_completed: number };

export const STUDENT_STATUSES = ['active', 'connected_only', 'no_activity'] as const;
export type StudentStatus = (typeof STUDENT_STATUSES)[number];

export type PedagogyStudentRow = {
    id: number;
    name: string;
    attached_to: string | null;
    status: StudentStatus;
    lessons_completed: number;
    exercises_submitted: number;
    projects_created: number;
    sessions: number;
    active_minutes: number;
    progress: Ratio;
    last_activity: IsoDateTime | null;
};

export type PedagogyResponse = Envelope & {
    tiles: PedagogyTiles;
    series: Series[];
    subjects: Table<PedagogySubjectRow>;
    students: Table<PedagogyStudentRow>;
};

export type PedagogyParams = CommonParams & {
    /** Filtre `status` ∈ `student_status` (§10.3). */
    students?: TableRequest<{ status?: string }>;
};

// ============================================================
// §4 — GET /ai
// ============================================================

export type ActorBreakdown = { calls: number; cost: Cost };

export type AiTiles = {
    calls: number;
    /** Administrateurs uniquement (D-20) : absent, jamais à zéro, sinon. */
    cost?: Cost;
    unpriced: { no_price: number; no_usage: number; models: string[] };
    tokens: { input: number; cached: number; output: number; reasoning: number };
    questions: number;
    contents: { quest: number; visual: number; exercise: number; illustration: number; reused: number };
    /** Administrateurs, périmètre « all » uniquement. */
    by_actor?: {
        student: ActorBreakdown;
        staff: ActorBreakdown;
        bench: ActorBreakdown;
        system: ActorBreakdown;
        total: ActorBreakdown;
    };
    /** Administrateurs, périmètre « all » uniquement. */
    undated_legacy?: { calls: number };
};

export type AiResponse = Envelope & {
    tiles: AiTiles;
    series: Series[];
    models: Table;
    students: Table;
};

export type AiParams = CommonParams & {
    source: AiSourceFilter;
    models?: TableQuery;
    students?: TableQuery;
};

// ============================================================
// §5-6 — GET /traces et /traces/:call_id
// ============================================================

export const TRACE_STATUSES = ['ok', 'failed_api', 'timeout', 'cancelled', 'failed', 'abandoned', 'started'] as const;
export type TraceStatus = (typeof TRACE_STATUSES)[number];

export type TraceTiles = {
    /** `started` : appels en cours ou interrompus, comptés à part. */
    by_status: Record<TraceStatus, number>;
    by_outcome: Record<string, number>;
    reports: number;
};

/** Ligne de la liste des appels (§5 ; formes de `learner` et `tokens` : §10.6). */
export type TraceRow = {
    call_id: string;
    started_at: IsoDateTime;
    source: AiSource;
    operation: string;
    model: string | null;
    status: TraceStatus;
    outcome: string | null;
    learner: Learner;
    tokens: Tokens | null;
    duration_ms: number | null;
    quest_id: string | null;
    report_count: number;
    has_payload: boolean;
};

export type TraceReferential = {
    operations: Array<{ source: AiSource; code: string }>;
    statuses: string[];
    outcomes: string[];
};

export type TracesResponse = Envelope & {
    tiles: TraceTiles;
    list: Table<TraceRow>;
    referential: TraceReferential;
};

/** Filtres de la liste des traces : NON préfixés (§5). */
export type TracesFilters = {
    status?: string;
    outcome?: string;
    operation?: string;
    source?: AiSource;
    flagged?: boolean;
    quest_id?: string;
};

export type TracesParams = CommonParams & TracesFilters & { list?: TableQuery };

export type TracePayload = {
    system_prompt: string | null;
    user_prompt: string | null;
    raw_output: string | null;
    guard_events: Array<Record<string, unknown>>;
    template_id: string | null;
    intent: string | null;
    activity_shape: string | null;
    density_hint: string | null;
    document_id: string | null;
    objective_id: string | null;
    temperature: number | null;
    created_at: IsoDateTime;
};

/** Signalement d'une quête (§10.7) ; `reporter` null hors de la branche. */
export type TraceReport = {
    id: number;
    reporter: { id: number; name: string | null } | null;
    reporter_type: string | null;
    reason: string;
    comment: string | null;
    created_at: IsoDateTime;
    resolution: string | null;
};

/** Quête vue par l'élève (`session_log`), mêmes règles d'accès que la trace. */
export type TraceQuest = {
    snapshot: Record<string, unknown>;
    responses: Array<Record<string, unknown>>;
};

/** Détail d'un appel (§10.7) : champs de la liste à plat, plus le détail. */
export type TraceDetailResponse = TraceRow & {
    model_requested: string;
    model_served: string | null;
    error: string | null;
    parent_call_id: string | null;
    request_id: string | null;
    conversation_id: string | null;
    finished_at: IsoDateTime | null;
    payload: TracePayload | null;
    reports: TraceReport[];
    quest: TraceQuest | null;
};

// ============================================================
// §7 — GET /finance
// ============================================================

export type FinanceTiles = {
    revenue: MoneyMulti;
    refunds: MoneyMulti;
    invoices_issued: number;
    offered_grants: number;
    /** Stock au `as_of`. */
    pending: { families: MoneyMulti; schools: MoneyMulti };
    subscriptions_active: number;
    expiring_soon: { value: number; threshold_days: number };
    expired: number;
    awaiting_first_payment: number;
    offered_access: number;
    coverage: Ratio;
    arppu: MoneyMulti;
    abandoned_attempts: number;
    /** Administrateurs uniquement. */
    data_quality?: { reconciliation_gaps: number };
};

export type FinanceResponse = Envelope & {
    tiles: FinanceTiles;
    series: Series[];
    invoices: Table;
    payments: Table;
    subscriptions: Table;
};

export const INVOICE_STATUSES = ['ouverte', 'payee', 'annulee', 'remboursee', 'offerte'] as const;
export const INVOICE_NATURES = ['checkout', 'renouvellement', 'siege_ecole', 'echeance_org', 'octroi_admin'] as const;
export const PAYMENT_NATURES = ['encaissement', 'remboursement'] as const;
export const PAYMENT_PROVIDERS = ['cashplus', 'lemonsqueezy', 'virement', 'legacy'] as const;
export const SUBSCRIPTION_STATES = ['active', 'expiring_soon', 'expired', 'awaiting_first_payment', 'upcoming', 'never'] as const;

/** Les trois tableaux financiers sont paginés, chacun sous sa clé (§10.2-3). */
export type FinanceParams = CommonParams & {
    invoices?: TableRequest<{ status?: string; nature?: string }>;
    payments?: TableRequest<{ nature?: string; provider?: string }>;
    subscriptions?: TableRequest<{ state?: string }>;
};

// ============================================================
// §8 — GET /export/:dataset
// ============================================================

export const EXPORT_DATASETS = [
    'pedagogy_students',
    'ai_students',
    'ai_models',
    'traces',
    'finance_invoices',
    'finance_payments',
    'finance_subscriptions',
] as const;
export type ExportDataset = (typeof EXPORT_DATASETS)[number];

/** Tableau d'origine de chaque jeu de données exporté (§10.8). */
export const EXPORT_TABLE: Record<ExportDataset, string> = {
    pedagogy_students: 'students',
    ai_students: 'students',
    ai_models: 'models',
    traces: 'list',
    finance_invoices: 'invoices',
    finance_payments: 'payments',
    finance_subscriptions: 'subscriptions',
};

/** Tri, recherche et filtres propres d'un tableau, sans pagination. */
export type TableFilters = { sort?: string; order?: SortOrder; q?: string; [filter: string]: string | undefined };

/**
 * Mêmes paramètres et filtres que le tableau d'origine (§8, §10.8), sans
 * pagination : `table` est envoyé sous le préfixe du tableau d'origine.
 */
export type ExportParams = CommonParams & {
    lang: LangCode;
    /** `source` de l'IA (`all` compris) ou filtre de service des traces. */
    source?: AiSourceFilter;
    /** Filtres non préfixés de la liste des traces (§5). */
    traceFilters?: Omit<TracesFilters, 'source'>;
    /** Tri, recherche et filtres propres du tableau d'origine. */
    table?: TableFilters;
};

export type ExportFile = { blob: Blob; filename: string };

// ============================================================
// §9 — GET/POST /ai-prices
// ============================================================

/** Types d'appel tarifables (CHECK de ai_model_price ; OCR à la page depuis la migration 0136). */
export const API_KINDS = ['chat', 'embedding', 'stt', 'tts', 'ocr'] as const;

/** Champs de prix d'un tarif, tous en chaînes décimales. */
export const AI_PRICE_FIELDS = [
    'input_per_1m',
    'cached_input_per_1m',
    'output_per_1m',
    'reasoning_per_1m',
    'per_audio_minute',
    'per_1m_characters',
    // Migrations 0136-0138 : écriture en cache (Anthropic), sortie texte
    // d'un modèle vocal temps réel, OCR à la page.
    'cache_write_per_1m',
    'text_output_per_1m',
    'per_1k_pages',
] as const;
export type ApiKind = (typeof API_KINDS)[number];

export type AiModelPrice = {
    /** Fournisseur : clé du tarif avec le modèle (migration 0132). */
    provider: string;
    model: string;
    api_kind: ApiKind;
    effective_from: string;
    input_per_1m: DecimalString | null;
    cached_input_per_1m: DecimalString | null;
    output_per_1m: DecimalString | null;
    reasoning_per_1m: DecimalString | null;
    per_audio_minute: DecimalString | null;
    per_1m_characters: DecimalString | null;
    cache_write_per_1m: DecimalString | null;
    text_output_per_1m: DecimalString | null;
    per_1k_pages: DecimalString | null;
    currency: string;
    source: string;
    created_by: number | null;
    created_at: string;
};

/** Clé de recherche du tarif manquant : fournisseur, modèle, type d'appel. */
export type UnpricedModel = {
    provider: string;
    model: string;
    api_kind: ApiKind;
    first_seen: string;
    last_seen: string;
    calls: number;
};

export type AiPricesResponse = {
    prices: AiModelPrice[];
    unpriced_models: UnpricedModel[];
};

/** Corps du POST : prix en chaînes décimales, devise explicite (aucun défaut). */
export type AiPriceInput = {
    provider: string;
    model: string;
    api_kind: ApiKind;
    effective_from: IsoDate;
    input_per_1m: DecimalString | null;
    cached_input_per_1m: DecimalString | null;
    output_per_1m: DecimalString | null;
    reasoning_per_1m: DecimalString | null;
    per_audio_minute: DecimalString | null;
    per_1m_characters: DecimalString | null;
    cache_write_per_1m: DecimalString | null;
    text_output_per_1m: DecimalString | null;
    per_1k_pages: DecimalString | null;
    currency: string;
};

// ============================================================
// Erreurs (contrat §1)
// ============================================================

export type ApiErrorCode =
    | 'invalid_period'
    | 'invalid_date'
    | 'window_too_large'
    | 'invalid_scope'
    | 'scope_type_mismatch'
    | 'invalid_filter'
    | 'invalid_pagination'
    | 'role_required'
    | 'scope_forbidden'
    | 'scope_not_found'
    | 'not_found'
    // POST /ai-prices (lot 8)
    | 'invalid_price'
    | 'price_exists'
    | 'unsupported_media_type';

// ============================================================
// Construction des paramètres
// ============================================================

type ParamValue = string | number | boolean | undefined | null;

/**
 * Aplatit les paramètres en chaîne de requête :
 * - un objet imbriqué devient des clés préfixées par sa clé (§10.2 :
 *   `{students: {sort: 'name'}}` → `students.sort=name`) ;
 * - les valeurs absentes ne sont pas envoyées ;
 * - `scope_id` n'est envoyé que hors périmètre « all » (contrat §1) ;
 * - un filtre booléen éteint n'est pas un filtre : seul `true` est envoyé.
 */
export const toQueryParams = (params: object): Record<string, string> => {
    const flat: Record<string, ParamValue> = {};
    const walk = (node: object, prefix: string) => {
        Object.entries(node).forEach(([k, v]) => {
            const key = prefix ? `${prefix}.${k}` : k;
            if (v !== null && typeof v === 'object') walk(v as object, key);
            else flat[key] = v as ParamValue;
        });
    };
    walk(params, '');
    const out: Record<string, string> = {};
    Object.entries(flat).forEach(([key, value]) => {
        if (value === undefined || value === null || value === '') return;
        if (key === 'scope_id' && flat.scope_type === 'all') return;
        if (typeof value === 'boolean') {
            if (value) out[key] = 'true';
            return;
        }
        out[key] = String(value);
    });
    return out;
};

/** Paramètres d'un export : ceux du tableau d'origine sous son préfixe (§10.8). */
export const exportQueryParams = (dataset: ExportDataset, params: ExportParams): Record<string, string> => {
    const { table, traceFilters, ...rest } = params;
    return toQueryParams({ ...rest, ...(traceFilters ?? {}), ...(table ? { [EXPORT_TABLE[dataset]]: table } : {}) });
};

/**
 * Nom du fichier exporté : celui du `Content-Disposition` du serveur. S'il
 * n'est pas lisible (en-tête non exposé par CORS), on reprend la règle du
 * contrat §8 `<dataset>_<scope>_<from>_<to>.csv`.
 */
export const exportFilename = (
    contentDisposition: string | undefined,
    dataset: ExportDataset,
    params: CommonParams
): string => {
    if (contentDisposition) {
        const star = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
        if (star) return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
        const plain = /filename="?([^";]+)"?/i.exec(contentDisposition);
        if (plain) return plain[1].trim();
    }
    const scope = params.scope_type === 'all' ? 'all' : `${params.scope_type}-${params.scope_id}`;
    return `${dataset}_${scope}_${params.from}_${params.to}.csv`;
};

// ============================================================
// Fonctions par endpoint
// ============================================================

export type MonitoringV2Api = ReturnType<typeof buildMonitoringV2Api>;

export const buildMonitoringV2Api = (client: AxiosInstance) => ({
    getScopes: async (signal?: AbortSignal): Promise<ScopesResponse> => {
        const { data } = await client.get<ScopesResponse>(ENDPOINTS_MONITORING_V2.SCOPES, { signal });
        return data;
    },
    getPedagogy: async (params: PedagogyParams, signal?: AbortSignal): Promise<PedagogyResponse> => {
        const { data } = await client.get<PedagogyResponse>(ENDPOINTS_MONITORING_V2.PEDAGOGY, {
            params: toQueryParams(params),
            signal,
        });
        return data;
    },
    getAi: async (params: AiParams, signal?: AbortSignal): Promise<AiResponse> => {
        const { data } = await client.get<AiResponse>(ENDPOINTS_MONITORING_V2.AI, {
            params: toQueryParams(params),
            signal,
        });
        return data;
    },
    getTraces: async (params: TracesParams, signal?: AbortSignal): Promise<TracesResponse> => {
        const { data } = await client.get<TracesResponse>(ENDPOINTS_MONITORING_V2.TRACES, {
            params: toQueryParams(params),
            signal,
        });
        return data;
    },
    getTrace: async (callId: string, signal?: AbortSignal): Promise<TraceDetailResponse> => {
        const { data } = await client.get<TraceDetailResponse>(ENDPOINTS_MONITORING_V2.trace(callId), { signal });
        return data;
    },
    getFinance: async (params: FinanceParams, signal?: AbortSignal): Promise<FinanceResponse> => {
        const { data } = await client.get<FinanceResponse>(ENDPOINTS_MONITORING_V2.FINANCE, {
            params: toQueryParams(params),
            signal,
        });
        return data;
    },
    /** Le CSV est produit par le serveur (D-25) : le front ne fait que le télécharger. */
    exportDataset: async (dataset: ExportDataset, params: ExportParams, signal?: AbortSignal): Promise<ExportFile> => {
        const response = await client.get<Blob>(ENDPOINTS_MONITORING_V2.exportDataset(dataset), {
            params: exportQueryParams(dataset, params),
            responseType: 'blob',
            signal,
        });
        const disposition = response.headers?.['content-disposition'] as string | undefined;
        return { blob: response.data, filename: exportFilename(disposition, dataset, params) };
    },
    getAiPrices: async (signal?: AbortSignal): Promise<AiPricesResponse> => {
        const { data } = await client.get<AiPricesResponse>(ENDPOINTS_MONITORING_V2.AI_PRICES, { signal });
        return data;
    },
    createAiPrice: async (input: AiPriceInput): Promise<AiModelPrice> => {
        const { data } = await client.post<AiModelPrice>(ENDPOINTS_MONITORING_V2.AI_PRICES, input);
        return data;
    },
});
